/** @jsx jsx */
import {
	useEffect, useRef, useMemo, useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Global, jsx, CacheProvider } from '@emotion/react';
import { prefixer } from 'stylis';
import createCache from '@emotion/cache';
import { buildStyles } from '../../theme/utils';
import tailwindCss from '../../css/tailwind-css';
import indexCss from '../../css/index-css';
import dayPickerCss from '../../css/day-picker-css';
import IF from '../helpers/IF';

const getFontImports = (fonts = []) => fonts.map((src) => <Global styles={`@import url('${src}');`} key={src} />);

// Create a shared stylesheet for better performance
let sharedStyleSheet = null;
const getStyleSheet = () => {
	if (!sharedStyleSheet && 'CSSStyleSheet' in window) {
		sharedStyleSheet = new CSSStyleSheet();
		sharedStyleSheet.replaceSync(tailwindCss + indexCss + dayPickerCss);
	}
	return sharedStyleSheet;
};

function GlobalStyles({ children, useShadow, theme }) {
	const hostRef = useRef(null);
	const shadowRootRef = useRef(null);
	const [shadowReady, setShadowReady] = useState(false);

	useEffect(() => {
		if (useShadow && hostRef.current && !shadowRootRef.current) {
			shadowRootRef.current = hostRef.current.attachShadow({ mode: 'open' });

			// Use adoptedStyleSheets for better performance (modern browsers)
			if (shadowRootRef.current && 'adoptedStyleSheets' in shadowRootRef.current) {
				const styleSheet = getStyleSheet();
				if (styleSheet) {
					shadowRootRef.current.adoptedStyleSheets = [styleSheet];
				}
			} else {
				// Fallback for older browsers
				const style = document.createElement('style');
				style.textContent = tailwindCss + indexCss;
				shadowRootRef.current.appendChild(style);
			}

			// Mark shadow root as ready
			setShadowReady(true);
		} else if (!useShadow) {
			// Reset shadow ready state when not using shadow
			setShadowReady(false);
		}
	}, [useShadow]);

	const styleCache = useMemo(() => createCache({
		key: 'buddy',
		stylisPlugins: [prefixer],
		...(shadowRootRef.current ? { container: shadowRootRef.current } : {}),
	}), [shadowReady]); // Use shadowReady instead of shadowRootRef.current since it's a primitive value

	const { webFonts, styles } = useMemo(() => buildStyles(theme, useShadow), [theme, useShadow]);
	const fontImports = useMemo(() => getFontImports(webFonts), [webFonts]);

	const content = (
		<CacheProvider value={styleCache}>
			{fontImports}
			<Global styles={styles} />
			{children}
		</CacheProvider>
	);

	return (
		<div id="offer-element-style-root" ref={hostRef}>
			<IF condition={useShadow}>
				{(shadowReady && shadowRootRef.current
					? createPortal(content, shadowRootRef.current)
					: null
				)}
			</IF>
			<IF condition={!useShadow}>
				{content}
			</IF>
		</div>
	);
}

export default GlobalStyles;
